import _ from 'lodash';
import {useCallback, useState} from 'react';

import * as CustomRunColorsActions from '../../../state/views/customRunColors/actions';
import * as CustomRunColorsTypes from '../../../state/views/customRunColors/types';
import * as CustomRunNamesActions from '../../../state/views/customRunNames/actions';
import * as CustomRunNamesTypes from '../../../state/views/customRunNames/types';
import * as GroupSelectionsActions from '../../../state/views/groupSelections/actions';
import * as GroupSelectionsTypes from '../../../state/views/groupSelections/types';
import * as ViewHooks from '../../../state/views/hooks';
import {Ref} from '../../../state/views/runSet/types';
import * as SemanticLegendTypes from '../../../state/views/semanticLegend/types';
import * as TempSelectionsActions from '../../../state/views/tempSelections/actions';
import * as TempSelectionsTypes from '../../../state/views/tempSelections/types';
import {BenchmarkProject} from '../../../types/graphql';
import {runColor} from '../../../util/colors';
import {WBTableRowWithRunSetInfo} from '../../../util/runhelpers';
import * as Run from '../../../util/runs';
import * as RunTypes from '../../../util/runTypes';
import * as SM from '../../../util/selectionmanager';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {JobInfo} from '../../Launch/utilTypes';
import {useSemanticLegend} from '../../WorkspaceDrawer/Settings/semanticLegend/useSemanticLegend';
import {DisabledFeaturesMap, RunRenameScope} from '../types';
export interface RunSelectorRowHeaderProps {
  benchmarkMeta?: BenchmarkProject;
  className?: string;
  customRunColorsRef: CustomRunColorsTypes.Ref;
  customRunNamesRef: CustomRunNamesTypes.Ref;
  disabledFeatures?: DisabledFeaturesMap;
  disableRunLink?: boolean;
  displayBenchmark?: boolean;
  displayTempCheckbox: boolean;
  entityName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  isFirstActiveRun?: boolean;
  isGroup?: boolean; // false if row is individual run, true if it's a group of runs
  isGroupExpanded: boolean;
  isInReport?: boolean;
  isLaunchActive?: boolean;
  isSingleMode?: boolean;
  isTableCollapsed?: boolean;
  loadingChildren?: boolean;
  onGroupButtonClick?(): void;
  onSelectRunName?: (runId: string) => void;
  onTempCheckboxClick(): void;
  projectName: string;
  recursionDepth: number;
  row: WBTableRowWithRunSetInfo;
  runSetRef: Ref;
  saveRunName(run: RunTypes.Run, newName: string): void;
  selectedRunName?: string;
  semanticLegendRef: SemanticLegendTypes.Ref;
  setLaunchDrawerRow(run: string, jobInfo: JobInfo): void;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  stopRun(id: string): Promise<any>; // from withMutations
  tempSelectionsRef: TempSelectionsTypes.Ref;
  truncationType: TruncationType;
}

export function useRunSelectorRowHeaderProps(props: RunSelectorRowHeaderProps) {
  const grouping = ViewHooks.usePartMapped(
    props.groupSelectionsRef,
    groupSelections => groupSelections.grouping
  );
  const expandedRowAddresses = ViewHooks.usePartMapped(
    props.groupSelectionsRef,
    groupSelections => groupSelections.expandedRowAddresses
  );
  const visCheckboxState = ViewHooks.usePartMapped(
    props.groupSelectionsRef,
    groupSelections => {
      return SM.getCheckedState(
        groupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  );

  // For some reason TS infers the type as `boolean` without the
  // type cast even though `runIsInBounds()` returns `boolean | null`
  const runIsInBounds = ViewHooks.usePartMapped(
    props.groupSelectionsRef,
    groupSelections => {
      return SM.runIsInBounds(
        groupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  ) as boolean | null;

  // TODO(adrnswanberg): This might not be the most efficient thing to do..
  const isAnyCellSelected = ViewHooks.usePartMapped(
    props.tempSelectionsRef,
    state =>
      !(state.bounds.length === 0 && state.tree.length === 0) ||
      state.root === SM.Root.All
  );
  const tempCheckboxState = ViewHooks.usePartMapped(
    props.tempSelectionsRef,
    state => {
      const tempGroupSelections = {
        grouping,
        selections: state,
        expandedRowAddresses,
      };
      return SM.getCheckedState(
        tempGroupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  );

  const toggleSelection = ViewHooks.useViewAction(
    props.groupSelectionsRef,
    GroupSelectionsActions.toggleSelection
  );

  const tempSelectToggle = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectToggle,
    props.tempSelectionsRef,
    props.groupSelectionsRef,
    props.row,
    props.recursionDepth + 1
  );

  const uniqueId = Run.uniqueId(props.row, grouping);
  const runSet = ViewHooks.usePartMaybe(props.runSetRef);
  const filters = ViewHooks.usePartMaybe(runSet?.filtersRef);
  const semanticLegendFromRef = ViewHooks.usePartMaybe(props.semanticLegendRef);
  const semanticLegendSettings = useSemanticLegend(
    props.entityName,
    props.projectName,
    filters,
    undefined,
    runSet?.search,
    semanticLegendFromRef
  );
  const customRunColor = runColor(
    props.row,
    grouping,
    ViewHooks.usePartMapped(
      props.customRunColorsRef,
      runColors => runColors[uniqueId]
    ),
    semanticLegendSettings
  );

  const setCustomRunColor = ViewHooks.useViewAction(
    props.customRunColorsRef,
    CustomRunColorsActions.setCustomRunColor
  );
  const setCustomRunColorCurried = useCallback(
    (color: string) => {
      setCustomRunColor(uniqueId, color);
    },
    [uniqueId, setCustomRunColor]
  );

  const customRunName = ViewHooks.usePartMapped(
    props.customRunNamesRef,
    customRunNames =>
      Run.getCustomRunName(
        props.row.name,
        Boolean(props.isGroup),
        customRunNames
      )
  );
  const setCustomRunName = ViewHooks.useViewAction(
    props.customRunNamesRef,
    CustomRunNamesActions.setCustomRunName
  );
  const setCustomRunNameCurried = useCallback(
    (customName: string) => {
      if (!props.isGroup) {
        setCustomRunName(props.row.name, customName);
      }
    },
    [props.isGroup, props.row.name, setCustomRunName]
  );

  const groupedBy = grouping[props.recursionDepth];
  const isRunGroup = _.isEqual(groupedBy, {section: 'run', name: 'group'});

  const [stopModalOpen, setStopModalOpen] = useState(false);
  const openStopModal = useCallback(
    () => setStopModalOpen(true),
    [setStopModalOpen]
  );
  const closeStopModal = useCallback(
    () => setStopModalOpen(false),
    [setStopModalOpen]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(
    () => setDeleteModalOpen(true),
    [setDeleteModalOpen]
  );
  const closeDeleteModal = useCallback(
    () => setDeleteModalOpen(false),
    [setDeleteModalOpen]
  );

  const {id} = props.row;
  const {stopRun} = props;
  const stopRunCurried = useCallback(() => stopRun(id), [stopRun, id]);

  const [editingNameScope, setEditingNameScope] = useState<
    RunRenameScope | undefined
  >();
  const startEditingName = useCallback(
    (scope: RunRenameScope) => setEditingNameScope(scope),
    [setEditingNameScope]
  );
  const stopEditingName = useCallback(
    () => setEditingNameScope(undefined),
    [setEditingNameScope]
  );

  return {
    closeDeleteModal,
    closeStopModal,
    customRunColor,
    customRunName,
    setDeleteModalOpen,
    deleteModalOpen,
    editingNameScope,
    groupedBy,
    isAnyCellSelected,
    isRunGroup,
    openDeleteModal,
    openStopModal,
    runIsInBounds,
    setCustomRunColor: setCustomRunColorCurried,
    setCustomRunName: setCustomRunNameCurried,
    setLaunchDrawerRow: props.setLaunchDrawerRow,
    startEditingName,
    stopEditingName,
    stopModalOpen,
    stopRun: stopRunCurried,
    tempCheckboxState,
    tempSelectToggle,
    toggleSelection,
    uniqueId,
    visCheckboxState,
  };
}
